import React, { useState } from "react";
import { FiLock, FiArrowLeft, FiCheckCircle, FiEye, FiEyeOff } from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../baseurl";
import { Link, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function ResetPassword() {
  const { token } = useParams(); // Extracts the hex token from the URL
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return Swal.fire("Error", "Passwords do not match!", "error");
    }

    if (password.length < 6) {
      return Swal.fire("Error", "Password must be at least 6 characters.", "warning");
    }

    setLoading(true);

    try {
      const { data } = await axios.post(`${baseurl}resetPassword`, { 
        token, 
        password 
      });

      if (data.success) {
        setIsSuccess(true);
        Swal.fire({
          icon: "success",
          title: "Password Updated!",
          text: "You can now login with your new password.",
          timer: 3000,
          showConfirmButton: false,
        });
        // Auto redirect after 3 seconds
        setTimeout(() => navigate("/"), 3500);
      } else {
        Swal.fire("Error", data.message || "Invalid or expired token", "error");
      }
    } catch (err) {
      console.error("Reset PW Error:", err);
      Swal.fire("Error", "Session expired or invalid token. Please request a new link.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-card shadow-sm">
        {!isSuccess ? (
          <>
            <div className="text-center mb-4">
              <div className="icon-circle mb-3">
                <FiLock size={32} color="#2874f0" />
              </div>
              <h4 className="fw-bold">Create New Password</h4>
              <p className="text-muted small">
                Please enter a strong password to secure your account.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* New Password */}
              <div className="mb-3">
                <label className="form-label small fw-bold">New Password</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FiLock className="text-muted" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control bg-light border-start-0 border-end-0"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span 
                    className="input-group-text bg-light border-start-0" 
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </span>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <label className="form-label small fw-bold">Confirm New Password</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FiLock className="text-muted" />
                  </span>
                  <input
                    type="password"
                    className="form-control bg-light border-start-0"
                    placeholder="Repeat password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 py-2 fw-bold"
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  "UPDATE PASSWORD"
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <FiCheckCircle size={60} color="#388e3c" className="mb-3" />
            <h4 className="fw-bold">All Set!</h4>
            <p className="text-muted small mb-4">
              Your password has been reset successfully. Redirecting you to login...
            </p>
            <Link to="/login" className="btn btn-primary w-100 py-2 fw-bold">
              LOGIN NOW
            </Link>
          </div>
        )}

        {!isSuccess && (
          <div className="text-center mt-4 pt-3 border-top">
            <Link to="/login" className="text-decoration-none small fw-bold d-flex align-items-center justify-content-center gap-1">
              <FiArrowLeft /> Back to Login
            </Link>
          </div>
        )}
      </div>

      <style>{`
        .auth-page-wrapper {
          background-color: #f1f3f6;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: 'Inter', sans-serif;
        }

        .auth-card {
          background: #ffffff;
          width: 100%;
          max-width: 420px;
          padding: 40px 30px;
          border-radius: 12px;
        }

        .icon-circle {
          width: 70px;
          height: 70px;
          background: #f0f5ff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .form-label { color: #666; }

        .form-control:focus {
          box-shadow: none;
          border-color: #2874f0;
          background-color: #fff !important;
        }

        .btn-primary {
          background-color: #fb641b; /* Flipkart Orange */
          border: none;
          transition: 0.3s;
        }

        .btn-primary:hover {
          background-color: #f4511e;
          transform: translateY(-1px);
        }

        .text-primary { color: #2874f0 !important; }
      `}</style>
    </div>
  );
}

export default ResetPassword;